import { getEntityTypeConfig } from '../lib/categories';

// PDF Editor Types
export type RedactionTool = 'select' | 'blackout' | 'highlight' | 'box';

export interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Redaction {
  id: string;
  type: RedactionTool;
  page: number;
  bounds: BoundingBox;
  color?: string;
  opacity?: number;
  text?: string;
  createdAt: Date;
}

export interface TextBlock {
  text: string;
  bounds: BoundingBox;
  page: number;
  fontName?: string;
  fontSize?: number;
}

export interface PDFDocumentInfo {
  numPages: number;
  title?: string;
  author?: string;
  pageWidth: number;
  pageHeight: number;
}

export type ZoomLevel = 25 | 50 | 75 | 100 | 125 | 150 | 175 | 200;

export interface EditorCommand {
  id: string;
  type: 'add' | 'remove' | 'modify';
  redaction: Redaction;
  previousState?: Redaction;
  execute: () => void;
  undo: () => void;
}

export interface PDFViewerState {
  currentPage: number;
  zoom: number;
  zoomMode: ZoomLevel;
  activeTool: RedactionTool;
  redactions: Redaction[];
  selectedRedaction: string | null;
  isLoading: boolean;
  error: string | null;
}

// AI Entity Detection Types
// The built-in types (server's entity_types.py). A tenant's detection categories
// are editable (sc-11031), so an entity type can be any category key — see
// lib/categories.ts for labels and colors.
export type BuiltInEntityType =
  // SpaCy NER types
  | 'PERSON'
  | 'ORG'
  | 'GPE'
  | 'LOC'
  | 'DATE'
  | 'TIME'
  | 'MONEY'
  | 'PERCENT'
  | 'CARDINAL'
  | 'ORDINAL'
  | 'QUANTITY'
  | 'EVENT'
  | 'FAC'
  | 'LANGUAGE'
  | 'LAW'
  | 'NORP'
  | 'PRODUCT'
  | 'WORK_OF_ART'
  // Regex pattern types
  | 'SSN'
  | 'CREDIT_CARD'
  | 'PHONE'
  | 'EMAIL'
  | 'BANK_ACCOUNT'
  | 'ADDRESS'
  | 'MEDICAL'
  // EntityRuler types (government IDs)
  | 'PASSPORT'
  | 'DRIVERS_LICENSE'
  | 'EIN'
  | 'ITIN'
  | 'MILITARY_ID'
  | 'CASE_NUMBER'
  | 'GOV_EMPLOYEE_ID'
  | 'MEDICARE_NUMBER'
  | 'VA_FILE_NUMBER'
  | 'STATE_ID'
  | 'VISA_NUMBER'
  | 'GREEN_CARD'
  // Dictionary match
  | 'DICTIONARY'
  // Custom
  | 'CUSTOM'
  // LLM catch-all for PII that doesn't fit a known type
  | 'OTHER';

/** A built-in type or any tenant category key (sc-11031). */
// eslint-disable-next-line @typescript-eslint/ban-types -- keeps built-in autocomplete on an open string type
export type EntityType = BuiltInEntityType | (string & {});

/** One of the tenant's detection categories (sc-11031); `key` is the entity type. */
export interface DetectionCategory {
  key: string;
  label: string;
  description: string;
  color: string;
  enabled: boolean;
  patterns: string[];
  /** Values reviewers retyped TO this category (FB-1012), newest last. */
  examples?: string[];
  /** Values reviewers retyped AWAY from this (tenant-created) category, newest last. */
  counter_examples?: string[];
}

export type EngineSource = 'spacy' | 'presidio' | 'llm' | 'llm-ollama' | 'dictionary' | 'manual' | 'manual_search' | 'ai' | 'johnsnowlabs';

export const ENGINE_COLOR_CONFIG: Record<EngineSource, { color: string; label: string }> = {
  spacy: { color: '#3B82F6', label: 'spaCy' },
  presidio: { color: '#F97316', label: 'Presidio' },
  llm: { color: '#8B5CF6', label: 'LLM' },
  'llm-ollama': { color: '#06B6D4', label: 'LLM (Ollama)' },
  johnsnowlabs: { color: '#DC2626', label: 'JohnSnowLabs' },
  dictionary: { color: '#22C55E', label: 'Dictionary' },
  manual: { color: '#6B7280', label: 'Manual' },
  manual_search: { color: '#EA580C', label: 'Case search' },
  ai: { color: '#6B7280', label: 'AI (legacy)' },
};

export interface EntitySuggestion {
  // Stable per-entity identity. Used everywhere for addressing/selection/keys —
  // NOT `index`, which is a positional value that shifts as entities are added
  // or reordered (entities live in a per-page collection server-side).
  entityId: string;
  index: number;
  entityType: EntityType;
  text: string;
  confidence: number;
  page: number;
  bounds?: BoundingBox;
  replacementText?: string;
  rationale?: string;
  status: 'pending' | 'approved' | 'rejected';
  source?: EngineSource;
  dictionarySource?: 'global' | 'case' | 'file';
  dictionaryAction?: 'always_redact' | 'always_allow';
  manuallyOverridden?: boolean;
  startPos?: number;
  // Server-computed grouping: occurrences sharing groupKey are case/fuzzy
  // variants of the same value and collapse into one row in the results panel.
  groupKey?: string;
  groupLabel?: string;
  /** For a detected DATE: why it matters (DOB, BANKING, PHI, FERPA, PERSONNEL), or
   *  NONE when no protected words are next to it (not suggested). See lib/dateContext. */
  dateContext?: string | null;
  isOptimistic?: boolean;
  // True while the text inside a manually-drawn region is being fetched from
  // the server; the suggestions panel shows a spinner until it resolves.
  textPending?: boolean;
  // Row optimistic-concurrency stamp from the server. Echoed back on mutations
  // so a row that changed since we read it 409s (stale tab / concurrent
  // reviewer) instead of being silently overwritten.
  version?: number;
}

// Word Dictionary Types
export interface WordDictionaryEntry {
  id: string;
  tenant_id: string;
  scope_type: 'global' | 'case' | 'file';
  scope_id?: string;
  word: string;
  match_type: 'exact' | 'contains';
  action: 'always_redact' | 'always_allow';
  reason?: string;
  // Optional grouping set by the list import ("City websites"); matching ignores it.
  category?: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface RedactionJob {
  jobId: string;
  documentId: string;
  status: 'pending' | 'processing' | 'awaiting_approval' | 'approved' | 'completed' | 'failed' | 'cancelled';
  progress: number;
  entitiesDetected: number;
  entitiesApproved: number;
  entitiesRejected: number;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  errorMessage?: string;
}

// Built-in entity type display configurations. Labels and colors on screen come
// from the tenant's categories (lib/categories.ts getEntityTypeConfig); this is
// the fallback until they load, and the source of the system types' (OTHER,
// CUSTOM, DICTIONARY) look.
export const ENTITY_TYPE_CONFIG: Record<BuiltInEntityType, { label: string; color: string; icon: string }> = {
  // SpaCy NER types
  PERSON: { label: 'Name', color: '#EF4444', icon: 'user' },
  ORG: { label: 'Organization', color: '#14B8A6', icon: 'building' },
  GPE: { label: 'Location (GPE)', color: '#EC4899', icon: 'map-pin' },
  LOC: { label: 'Location', color: '#F472B6', icon: 'map-pin' },
  DATE: { label: 'Date', color: '#6366F1', icon: 'calendar' },
  TIME: { label: 'Time', color: '#818CF8', icon: 'clock' },
  MONEY: { label: 'Money', color: '#22C55E', icon: 'dollar-sign' },
  PERCENT: { label: 'Percent', color: '#84CC16', icon: 'percent' },
  CARDINAL: { label: 'Number', color: '#A3A3A3', icon: 'hash' },
  ORDINAL: { label: 'Ordinal', color: '#D4D4D4', icon: 'list-ordered' },
  QUANTITY: { label: 'Quantity', color: '#FBBF24', icon: 'scale' },
  EVENT: { label: 'Event', color: '#F97316', icon: 'calendar-days' },
  FAC: { label: 'Facility', color: '#FB923C', icon: 'building-2' },
  LANGUAGE: { label: 'Language', color: '#A78BFA', icon: 'globe' },
  LAW: { label: 'Law', color: '#C084FC', icon: 'scale' },
  NORP: { label: 'Group', color: '#E879F9', icon: 'users' },
  PRODUCT: { label: 'Product', color: '#F0ABFC', icon: 'package' },
  WORK_OF_ART: { label: 'Work of Art', color: '#D946EF', icon: 'palette' },

  // Regex pattern types
  SSN: { label: 'SSN', color: '#F59E0B', icon: 'shield' },
  CREDIT_CARD: { label: 'Credit Card', color: '#8B5CF6', icon: 'credit-card' },
  PHONE: { label: 'Phone', color: '#10B981', icon: 'phone' },
  EMAIL: { label: 'Email', color: '#3B82F6', icon: 'mail' },
  BANK_ACCOUNT: { label: 'Bank Account', color: '#059669', icon: 'landmark' },
  ADDRESS: { label: 'Address', color: '#EC4899', icon: 'map-pin' },
  MEDICAL: { label: 'Medical', color: '#DC2626', icon: 'heart-pulse' },

  // EntityRuler types (government IDs)
  PASSPORT: { label: 'Passport', color: '#7C3AED', icon: 'id-card' },
  DRIVERS_LICENSE: { label: 'Driver License', color: '#0891B2', icon: 'id-card' },
  EIN: { label: 'EIN', color: '#0D9488', icon: 'file-text' },
  ITIN: { label: 'ITIN', color: '#14B8A6', icon: 'file-text' },
  MILITARY_ID: { label: 'Military ID', color: '#065F46', icon: 'shield' },
  CASE_NUMBER: { label: 'Case Number', color: '#7C2D12', icon: 'folder' },
  GOV_EMPLOYEE_ID: { label: 'Gov Employee ID', color: '#1E40AF', icon: 'badge' },
  MEDICARE_NUMBER: { label: 'Medicare', color: '#1D4ED8', icon: 'heart' },
  VA_FILE_NUMBER: { label: 'VA File Number', color: '#1E3A8A', icon: 'file' },
  STATE_ID: { label: 'State ID', color: '#3730A3', icon: 'id-card' },
  VISA_NUMBER: { label: 'Visa Number', color: '#5B21B6', icon: 'plane' },
  GREEN_CARD: { label: 'Green Card', color: '#166534', icon: 'id-card' },

  // Dictionary match
  DICTIONARY: { label: 'Dictionary', color: '#F97316', icon: 'book' },

  // Custom
  CUSTOM: { label: 'Custom', color: '#64748B', icon: 'tag' },

  // LLM catch-all for PII that doesn't fit a known type
  OTHER: { label: 'Other PII', color: '#8B5CF6', icon: 'tag' },
};

export function getHighlightColor(
  suggestion: EntitySuggestion,
  isTesterMode: boolean,
): string {
  if (!isTesterMode) {
    return getEntityTypeConfig(suggestion.entityType).color;
  }
  const source = (suggestion.source || 'ai') as EngineSource;
  return ENGINE_COLOR_CONFIG[source]?.color || ENGINE_COLOR_CONFIG.ai.color;
}

// Case Management Types
export type CaseStatus = 'not_started' | 'in_progress' | 'completed';
export type DocumentStatus = 'unprocessed' | 'needs_redaction' | 'redacted' | 'completed';

export interface Case {
  id: string;
  name: string;
  status: CaseStatus;
  notes?: string;
  owner: string;
  owner_name?: string;
  tenant_id: string;
  document_count: number;
  created_at: string;
  updated_at: string;
  deleted_at?: string;
  metadata?: Record<string, unknown>;
  /** eDiscovery systems that have delivered files to this case (FB-1021). */
  ediscovery_systems?: string[];
}

// Metadata stamped on a Case that was auto-created from an external request
// (NextRequest, etc.). Mirrors the backend `build_request_metadata` shape.
export interface ExternalRequestMeta {
  source: 'request';
  request_provider?: string;
  external_request_id?: string;
  /** The request's number (e.g. "24-137"): the picker reads its page directly. */
  request_number?: string;
  request_status?: string;
  requester?: string;
  due_date?: string;
}

// Narrow a Case's free-form metadata to ExternalRequestMeta when it originated
// from an external request; returns null otherwise.
export function getRequestMeta(caseData: Pick<Case, 'metadata'>): ExternalRequestMeta | null {
  const meta = caseData.metadata as Record<string, unknown> | undefined;
  if (meta && meta.source === 'request') {
    return meta as unknown as ExternalRequestMeta;
  }
  return null;
}

// --- Case import wizard (eDiscovery intake) ---------------------------------
export type EDiscoverySystem = 'purview' | 'jatheon' | 'mock';
export type EDiscoverySourceStatus =
  | 'searching'
  | 'pending'
  | 'preparing'
  | 'ready'
  | 'ingesting'
  | 'ingested'
  | 'failed';
export type EDiscoveryFileStatus = 'pending' | 'downloading' | 'ingested' | 'failed';

export interface EDiscoveryFileEntry {
  external_id: string;
  name: string;
  size?: number | null;
  content_ref?: string | null;
  selected: boolean;
  document_id?: string | null;
  status: EDiscoveryFileStatus;
  error?: string | null;
}

export interface EDiscoverySource {
  id: string;
  case_id: string;
  system: EDiscoverySystem;
  external_case_id: string;
  status: EDiscoverySourceStatus;
  case_name?: string | null;
  /** Exports ticked in the wizard; null/absent = every export of the case. */
  export_ids?: string[] | null;
  /** Ingested by the server as soon as it is prepared ("Open case" in the wizard). */
  auto_ingest?: boolean;
  /** The latest search RedactIt ran in the system for this source (sc-11767). */
  search_run?: EDiscoverySearchRun | null;
  /** Exports ticked while an import was running: imported when it's done (a search alone never queues). */
  queued_export_ids?: string[];
  error?: string | null;
  files: EDiscoveryFileEntry[];
  created_at?: string | null;
  updated_at?: string | null;
}

/** Where a search RedactIt runs looks: the listed mailboxes and sites, the whole organization, or the case's data sources. */
export type EDiscoverySearchScope = 'listed' | 'tenant' | 'case';

/**
 * A search RedactIt runs in the eDiscovery system and imports (sc-11767):
 * estimating (counting hits) → exporting → done (the export is being imported).
 */
export interface EDiscoverySearchRun {
  search_id: string;
  name: string;
  query: string;
  scope: EDiscoverySearchScope;
  sources: string[];
  /** waiting: the export is ready, and is imported after the import that's running. */
  stage: 'estimating' | 'exporting' | 'waiting' | 'done' | 'failed';
  /** Why a failed run stopped. */
  error?: string | null;
  estimate_operation_id?: string | null;
  export_operation_id?: string | null;
  item_count?: number | null;
  size?: number | null;
  unindexed_count?: number | null;
  percent?: number | null;
  started_at?: string | null;
  finished_at?: string | null;
  started_by?: string | null;
}

/** A case in the external eDiscovery system, as the wizard's case search returns it. */
export interface EDiscoveryCaseSummary {
  external_id: string;
  name: string;
  status?: string | null;
}

/** One export of an external case (Purview status: notStarted/running/succeeded/failed/partiallySucceeded). */
export interface EDiscoveryExport {
  external_id: string;
  name: string;
  status?: string | null;
  created_at?: string | null;
  created_by?: string | null;
  /** Content packages (ZIP/PST archives), not documents — the report package is excluded. */
  package_count: number;
  total_size: number;
  /** Finished, but the system no longer serves its files (Purview purges exports after a few weeks). */
  expired?: boolean;
}

export interface EDiscoveryCaseExports {
  case: { external_id: string; name: string; portal_url?: string | null };
  exports: EDiscoveryExport[];
}

export interface RequestInfo {
  case_id: string;
  name: string;
  notes?: string | null;
  source?: string | null;
  request_provider?: string | null;
  external_request_id?: string | null;
  request_status?: string | null;
  requester?: string | null;
  due_date?: string | null;
}

// A NextRequest request as offered in the create-case dropdown.
export interface RequestSummary {
  external_id: string;
  /** Human-facing request number (NextRequest's pretty id, e.g. "24-137"). */
  number?: string | null;
  title: string;
  requester?: string | null;
  status?: string | null;
  due_date?: string | null;
}

/** Whether the create-case "From NextRequest" tab can be used (GET /intake/request-service). */
export interface RequestServiceStatus {
  provider?: string | null;
  state: 'connected' | 'not_configured' | 'not_connected' | 'disabled';
}

export interface RequestSearchResult {
  /** Newest first, open and closed. */
  requests: RequestSummary[];
  /** More requests match than the requested limit. */
  more?: boolean;
}

export interface ExternalRequestDetail extends RequestSummary {
  content?: string | null; // the request text → becomes Case.notes
  provider?: string | null;
  /** When the request was submitted (provider's request date). */
  submitted_at?: string | null;
  /** The request on NextRequest, when the site URL is known. */
  url?: string | null;
}

export interface TopEntity {
  /** Group key, shared with the Results panel's grouping. */
  key?: string;
  /** The group's display spelling. */
  text: string;
  entity_type: string;
  /** Occurrences across the case (every spelling). */
  count: number;
  /** Occurrences already redacted (approved). */
  redacted?: number;
  confidence?: number | null;
  /** Every spelling in the group, most common first. */
  spellings?: string[];
  /** A case or global always-redact rule already covers the group. */
  in_dictionary?: boolean;
  /** For a DATE: why it matters (DOB, PHI, ...; see lib/dateContext). */
  date_context?: string | null;
}

// Context awareness — non-trivial quasi-identifier correlation risks.
export interface RiskTerm {
  text: string;
  entity_type: string;
  /** For a DATE: why it matters (DOB, PHI, ...; see lib/dateContext). */
  date_context?: string | null;
  /** "redact": has to go to break the re-identification (preselected on the card);
   *  "context": only explains the risk. Missing on risks analysed before roles. */
  role?: 'redact' | 'context' | null;
  /** The case's or the tenant's Allow List keeps it, so it's never preselected (server-derived). */
  allow_listed?: boolean;
  /** A case or global always-redact rule already covers it (server-derived). */
  in_dictionary?: boolean;
}

export interface CorrelationRisk {
  rule_id: string;
  name: string;
  severity: 'high' | 'medium' | 'low';
  description: string;
  rationale: string;
  involved_terms: RiskTerm[];
  document_id?: string | null;
  score?: number;
  scope?: 'case' | 'general';
  source?: 'rules' | 'llm';
  /** Stable identity across re-analyses (rule + terms); dismissals are stored by it (FR-14). */
  key?: string;
  dismissed?: boolean;
}

export interface ContextAnalysis {
  status: 'none' | 'pending' | 'ready' | 'failed';
  rule_version?: string | null;
  llm_model?: string | null;
  error?: string | null;
  risks: CorrelationRisk[];
  suggested_terms: RiskTerm[];
  general_patterns?: CorrelationRisk[];
}

// A reusable correlation pattern promoted from a case's 'general' risk (C4),
// stripped of case-specific literal values. Tenant-global.
export interface SteeringHint {
  id: string;
  name: string;
  rationale: string;
  involved_types: string[];
  severity: string;
  source_case?: string | null;
  created_at?: string | null;
}

// Per-document redaction progress, reduced server-side from the document's jobs.
// Present only when the case is fetched with include_progress=true. Mirrors the
// backend DocumentProgress model and drives the sidebar progress bars.
export interface DocumentProgress {
  is_processing: boolean;
  is_failed: boolean;
  progress: number;
  estimated_seconds_remaining?: number | null;
  has_results: boolean;
  entities_detected: number;
  entities_approved: number;
  entities_rejected: number;
  // Status of the active job (queued/processing/rescanning), else null. Used to
  // tell a bulk-redaction rescan apart from a genuine initial scan.
  active_status?: string | null;
  // Why the newest job failed, when is_failed is true. Surfaced in the sidebar
  // next to the "Processing failed" state so the reviewer knows why.
  error_message?: string | null;
}

export interface CaseDocument {
  id: string;
  name: string;
  type: string;
  upload_date: string;
  status: DocumentStatus;
  file_size: number;
  page_count?: number;
  case_id?: string;
  parent_id?: string;           // For child documents (extracted from PST/email)
  children?: CaseDocument[];    // For tree structure in UI
  children_count?: number;      // Count of direct children
  is_container?: boolean;       // True for PST files that are containers only
  progress?: DocumentProgress;  // Redaction progress (only when include_progress=true)
  metadata?: Record<string, unknown>;  // Arbitrary document metadata (e.g. is_container flag)
  review_decision?: ReviewDecision | null;  // Reviewer decision (FR-01); absent = undecided
  review_decided_by?: string | null;
  review_decided_at?: string | null;
  // 'thread': tagged Responsive because an email above it in its thread was marked
  // Responsive (sc-11471); review_decision_source_id is that email. Absent = the reviewer's own.
  review_decision_source?: 'thread' | null;
  review_decision_source_id?: string | null;
  has_redacted_version?: boolean;  // A redacted file exists (what the redacted ZIP export holds)
  out_of_date?: boolean;  // Finalized, but a rule matching its text changed since (FR-14)
  stale_reasons?: string[];
}

export interface CaseWithDocuments extends Case {
  documents: CaseDocument[];
}

export interface CaseStatistics {
  total_documents: number;
  unprocessed: number;
  needs_redaction: number;
  redacted: number;
  completed: number;
  case_status: CaseStatus;
}

// Case status display configuration
export const CASE_STATUS_CONFIG: Record<CaseStatus, { label: string; color: string; bgColor: string }> = {
  not_started: { label: 'Not Started', color: '#6B7280', bgColor: '#F3F4F6' },
  in_progress: { label: 'In Progress', color: '#3B82F6', bgColor: '#DBEAFE' },
  completed: { label: 'Completed', color: '#10B981', bgColor: '#D1FAE5' },
};

// Document status display configuration
// What a document's badge says, and in which color (FB-1024 #4). `analyzing` and
// `failed` come from its latest redaction job; the rest are its review status.
// `unprocessed` reads "Suggestions ready" once detection has produced results
// (sc-11034) and `not_analyzed` before that (see editor/documentStage.ts).
// Red is kept for real failures. PROPOSAL — names and colors to confirm with product.
export type DocumentStage = 'not_analyzed' | 'analyzing' | DocumentStatus | 'failed';

export const DOCUMENT_STAGE_CONFIG: Record<DocumentStage, { label: string; color: string }> = {
  not_analyzed: { label: 'Not analyzed', color: '#9CA3AF' },
  analyzing: { label: 'Analyzing', color: '#6B7280' },
  unprocessed: { label: 'Suggestions ready', color: '#3B82F6' },
  needs_redaction: { label: 'Needs Redaction', color: '#F59E0B' },
  redacted: { label: 'Redacted', color: '#8B5CF6' },
  completed: { label: 'Completed', color: '#10B981' },
  failed: { label: 'Failed', color: '#EF4444' },
};

// Reviewer decision on a document (FR-01, Lake Stevens): does it answer the
// request? Separate from the redaction stage above and from the relevance score —
// the score only *suggests* a decision; nothing is stored until a reviewer picks.
export type ReviewDecision = 'responsive' | 'non_responsive' | 'needs_review';

export const REVIEW_DECISION_CONFIG: Record<ReviewDecision, { label: string; color: string }> = {
  responsive: { label: 'Responsive', color: '#047857' },
  non_responsive: { label: 'Non-responsive', color: '#6B7280' },
  needs_review: { label: 'Needs review', color: '#B45309' },
};

export const REVIEW_DECISIONS = Object.keys(REVIEW_DECISION_CONFIG) as ReviewDecision[];

/**
 * The decision a relevance score (0-100) suggests, if any: High relevance (the
 * list's >= 80 bucket) suggests responsive; a score under 20 — the model calling
 * the document irrelevant — suggests non-responsive. Display only, never stored.
 */
export function suggestedReviewDecision(relevanceScore: number | null | undefined): ReviewDecision | null {
  if (typeof relevanceScore !== 'number') return null;
  if (relevanceScore >= 80) return 'responsive';
  if (relevanceScore < 20) return 'non_responsive';
  return null;
}

// Redaction Reason Types
export interface RedactionReason {
  id: string;
  title: string;
  description: string;
  // Legal reference printed in the redaction log's reference table. Optional:
  // reasons created before these fields existed come back as "".
  citation?: string;
  legal_text?: string;
  url?: string;
  tenant_id: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

// =============================================================================
// PST (Outlook Personal Storage Table) Types
// =============================================================================

// PST Attachment metadata
export interface PSTAttachment {
  attachment_id: string;
  message_id: string;
  filename: string;
  content_type?: string;
  size_bytes: number;
}

// PST Message header (for lazy loading - minimal data for list view)
export interface PSTMessageHeader {
  message_id: string;
  message_index: number;  // 0-indexed position for navigation
  folder_path: string;
  subject?: string;
  from: string;           // Format: "Display Name" <email@domain.com>
  to: string;             // Format: "Display Name" <email@domain.com>
  cc?: string;
  date?: string;
  has_attachments: boolean;
  attachment_count: number;
}

// PST Message full content (loaded on demand)
export interface PSTMessage {
  message_id: string;
  message_index: number;
  subject?: string;
  from: string;           // Format: "Display Name" <email@domain.com>
  to: string;
  cc?: string;
  date?: string;
  html_body?: string;
  plain_body?: string;
  attachments: PSTAttachment[];
}

// PST Folder metadata
export interface PSTFolder {
  folder_id: string;
  name: string;
  path: string;
  parent_id?: string;
  message_count: number;
  subfolder_count: number;
}

// PST Structure overview (for initial navigation)
export interface PSTStructure {
  document_id: string;
  total_messages: number;
  total_folders: number;
  total_attachments: number;
  folders: PSTFolder[];
}

// PST Message list response (paginated)
export interface PSTMessageList {
  document_id: string;
  messages: PSTMessageHeader[];
  total: number;
  offset: number;
  limit: number;
}

// Extended EntitySuggestion with message index for PST files
export interface PSTEntitySuggestion extends EntitySuggestion {
  messageIndex: number;   // Which message this suggestion belongs to
  fieldType?: string;     // 'from', 'to', 'cc', 'subject', 'body', 'attachment'
}

// PST Entity location for batch redaction
export interface PSTEntityLocation {
  entity_type: string;
  text: string;
  confidence: number;
  message_id: string;
  message_index: number;
  field_type: string;
  attachment_id?: string;
  replacement_text?: string;
}

// PST Batch redaction response
export interface PSTBatchRedactionResponse {
  document_id: string;
  redacted_document_id?: string;
  messages_processed: number;
  entities_redacted: number;
  status: string;
  message: string;
}

// PST text extraction result (for entity detection)
export interface PSTExtractionResult {
  document_id: string;
  total_messages: number;
  messages_processed: number;
  combined_text: string;
  message_text_ranges: Record<string, [number, number]>;
  message_index_map: Record<string, number>;
}

// ---------------------------------------------------------------------------
// Admin: external-service connections (see server/routers/admin_integrations.py)
// ---------------------------------------------------------------------------

/** One credential field to render. Never carries a value. */
export interface IntegrationSlotField {
  name: string;
  label: string;
  /** string | url | password */
  field_type: string;
  /** Write-only: blank on update means "keep the stored value". */
  secret: boolean;
  help_text?: string | null;
}

/** One connection slot, resolved for the current tenant. */
export interface IntegrationSlot {
  key: string;
  label: string;
  purpose: string;
  integration_type: string;
  /** The integrations-manager lookup key, shown so it can be found in govern. */
  integration_name: string;
  fields: IntegrationSlotField[];
  connected: boolean;
  integration_id?: string | null;
  status?: string | null;
  health_status?: string | null;
  /** Something on this deploy uses the connection (see `used_by`). */
  feature_enabled: boolean;
  /** The features that use this connection on this deploy. */
  used_by?: string[];
  /** Whether this deploy routes to this provider at all. */
  provider_selected: boolean;
  /** Local env credentials shadow whatever is connected here. */
  overridden_by_env: boolean;
}

export interface IntegrationHealth {
  healthy: boolean;
  detail?: string | null;
}

// Review panel Finish tab (FR-14): GET /cases/{id}/review-status.
export type FinalizeMode = 'finalize' | 'refinalize';

export interface ReviewStatusDocument {
  id: string;
  name: string;
  review_decision: ReviewDecision | null;
  status: DocumentStatus;
  finalized: boolean;
  finalized_at: string | null;
  out_of_date: boolean;
  stale_reasons: string[];
  pending_suggestions: number;
  approved_suggestions: number;
  /** Why it can't be finalized without a reviewer; null when it can. */
  blocker: string | null;
  /** A Finalize / Re-finalize run from the panel, if one is queued, running or failed. */
  batch: { mode: FinalizeMode; status: 'queued' | 'running' | 'failed'; error: string | null } | null;
}

export interface ReviewStatus {
  case_id: string;
  documents: ReviewStatusDocument[];
}

export interface FinalizeBatchResult {
  queued: string[];
  needs_review: { id: string; name?: string; reason: string }[];
}

// Case search (FR-04, sc-11033): GET /cases/{id}/search and POST .../search/redact.
export type CaseSearchMode = 'loose' | 'exact';

export interface CaseSearchBox {
  page: number; // 0-indexed
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface CaseSearchHit {
  id: string;
  document_id: string;
  start: number;
  end: number;
  /** The text as it appears in the document (what matched). */
  text: string;
  before: string;
  after: string;
  /** Spelled differently from the query (beyond case), e.g. "7KLM 294" for "7KLM294". */
  variant: boolean;
  /** Index of this spelling among the document's same-text occurrences (HTML viewers). */
  occurrence: number;
  page: number | null;
  /** One box per line, PDF points; empty for text-based (DOCX/email) documents. */
  boxes: CaseSearchBox[];
  redacted: boolean;
  redactable: boolean;
}

export interface CaseSearchDocument {
  document_id: string;
  name: string;
  status: DocumentStatus;
  finalized: boolean;
  out_of_date: boolean;
  job_id: string;
  reviewed_as_pdf: boolean;
  truncated: boolean;
  hits: CaseSearchHit[];
}

export interface CaseSearchResult {
  query: string;
  mode: CaseSearchMode;
  total_hits: number;
  searched_documents: number;
  skipped_documents: { document_id: string; name: string; reason: string }[];
  truncated: boolean;
  documents: CaseSearchDocument[];
}

export interface CaseSearchRedactRequest {
  query: string;
  mode: CaseSearchMode;
  /** Omit for every hit ("Redact all"). */
  hit_ids?: string[];
  entity_type: string;
  reason?: string;
  add_case_rule?: boolean;
}

export interface CaseSearchRedactResult {
  redacted_hits: number;
  created_suggestions: number;
  documents: { document_id: string; hits: number }[];
  out_of_date_documents: { document_id: string; name: string }[];
  skipped: { hit_id: string; reason: string }[];
  rules_added: string[];
}

// ---------------------------------------------------------------------------
// CSV table mode (sc-11403). Every CSV is reviewed as a table: the unit of
// review is a rule (a column, a built-in detector, a category pattern, a
// dictionary word), and cell decisions are sparse overrides on top. Shapes mirror the server's
// /tables API (docs/csv-table-mode.md). Offsets are UTF-16 code units, i.e.
// plain JavaScript string indices.
// ---------------------------------------------------------------------------

/** How a redaction job reviews its document (RedactionJobStatus.review_mode). */
export type ReviewMode = 'document' | 'table';

export type TablePhase = 'queued' | 'indexing' | 'suggesting' | 'scanning' | 'ready' | 'failed';

/** `Document.metadata.table_mode`, set when a CSV's job is created in table mode. */
export interface TableModeMetadata {
  job_id: string;
  phase: TablePhase;
  row_count?: number | null;
  column_count?: number | null;
  error?: string | null;
  redaction_log_path?: string | null;
}

export interface TableColumn {
  index: number;
  name: string;
  non_empty: number;
  max_length: number;
}

export interface TableDialect {
  delimiter: string;
  quotechar: string;
  encoding: string;
  line_terminator: string;
}

export type TableAiStatus = 'pending' | 'done' | 'failed' | 'skipped';

export interface TableCounts {
  rules_total: number;
  rules_pending: number;
  rules_approved: number;
  rules_rejected: number;
  /** Rules that could not run (refused or out of time) and are not rejected yet: they block finalize. */
  rules_failed: number;
  cell_decisions: number;
  detected: number;
  approved: number;
  rejected: number;
}

/** T1 `GET /tables/{job_id}`. Row/column fields are null until indexing ends. */
export interface TableSummary {
  job_id: string;
  document_id: string;
  job_status: string;
  review_mode: 'table';
  phase: TablePhase;
  progress: number;
  estimated_seconds_remaining: number | null;
  error: string | null;
  row_count: number | null;
  column_count: number | null;
  has_header: boolean | null;
  columns: TableColumn[] | null;
  dialect: TableDialect | null;
  ai: { status: TableAiStatus; message: string | null };
  counts: TableCounts;
  rules_seq: number;
  redacted_available: boolean;
  read_only: boolean;
}

export type TableRuleKind = 'column' | 'pattern' | 'detector' | 'dictionary';
export type TableRuleOrigin = 'ai' | 'heuristic' | 'builtin' | 'category' | 'dictionary' | 'manual';
export type TableRuleStatus = 'pending' | 'approved' | 'rejected';
export type TableEvalState = 'ready' | 'failed';

export interface TableSample {
  row: number;
  col: number;
  text: string;
}

export interface TableRuleDictionary {
  entry_id: string;
  word: string;
  match_type: string;
  action: string;
  scope: string;
}

/** T3 rule. `reason` is the bare title; `replacement_text` is "[title]". */
export interface TableRule {
  rule_id: string;
  kind: TableRuleKind;
  origin: TableRuleOrigin;
  entity_type: EntityType;
  label: string;
  column: number | null;
  columns: number[] | null;
  pattern: string | null;
  flags: '' | 'i';
  detector: string | null;
  dictionary: TableRuleDictionary | null;
  confidence: number | null;
  rationale: string | null;
  status: TableRuleStatus;
  reason: string | null;
  replacement_text: string | null;
  eval_state: TableEvalState;
  eval_error: string | null;
  match_cells: number;
  match_rows: number;
  first_row: number | null;
  samples: TableSample[];
  version: number;
  decided_by: string | null;
  decided_at: string | null;
  created_at: string;
}

export type TableHitState = 'pending' | 'approved' | 'rejected';
export type TableHitDecidedBy = 'rule' | 'cell' | 'allow_rule' | 'dictionary';

/** One hit in a cell. Manual hits have `rule_id: null` and `kind: 'manual'`. */
export interface TableHit {
  row: number;
  col: number;
  start: number;
  end: number;
  rule_id: string | null;
  kind: TableRuleKind | 'manual';
  entity_type: EntityType;
  state: TableHitState;
  decided_by: TableHitDecidedBy | string;
}

export type TableCellAction = 'redact' | 'keep';

export interface TableCellDecision {
  row: number;
  col: number;
  rule_id: string | null;
  action: TableCellAction;
  /** null = the whole cell (only for rule_id=null + redact). */
  spans: [number, number][] | null;
  entity_type: EntityType | null;
  replacement_text: string | null;
  version: number;
  created_by: string;
  created_at: string;
}

/** What apply will write for a cell: the whole cell, or each merged span. */
export interface TableEffectiveCell {
  row: number;
  col: number;
  whole: boolean;
  spans: [number, number][];
  replacement: string;
}

export type TableRowsVersion = 'original' | 'redacted';

export interface TableRow {
  row: number;
  cells: string[];
}

/** The header record's row number (sc-11403): it is content, scanned and redactable like any row. */
export const TABLE_HEADER_ROW = -1;

/** T2 `GET /tables/{job_id}/rows`. */
export interface TableRowsResponse {
  start: number;
  limit: number;
  row_count: number;
  version: TableRowsVersion;
  rules_seq: number;
  /** The header record (row -1) on the first page, or null (no header, or a later page). */
  header?: TableRow | null;
  rows: TableRow[];
  hits: TableHit[];
  cell_decisions: TableCellDecision[];
  effective: TableEffectiveCell[];
}

export interface TableRulesResponse {
  rules: TableRule[];
  rules_seq: number;
}

/** T4: "Redact this column…". */
export interface CreateTableRuleRequest {
  kind: 'column';
  column: number;
  entity_type: EntityType;
  reason: string | null;
}

export interface TableRuleDecision {
  rule_id: string;
  status: TableRuleStatus;
  reason: string | null;
  expected_version: number | null;
}

/** T6/T7 response. */
export interface TableRuleDecisionsResponse {
  rules: TableRule[];
  counts: TableCounts;
  rules_seq: number;
}

/** T9 response: row indexes in `direction` order. */
export interface TableRowListResponse {
  rows: number[];
  has_more: boolean;
}

export type TableNavDirection = 'next' | 'prev';

/** One T11 decision. `clear` deletes the override for (row, col, rule_id). */
export type TableCellDecisionInput =
  | {
      row: number;
      col: number;
      rule_id: string;
      action: TableCellAction | 'clear';
      expected_version?: number | null;
    }
  | {
      row: number;
      col: number;
      rule_id: null;
      action: TableCellAction | 'clear';
      spans?: [number, number][] | null;
      entity_type?: EntityType | null;
      reason?: string | null;
      expected_version?: number | null;
    };

/** T11 response. */
export interface TableCellDecisionsResponse {
  decisions: TableCellDecision[];
  counts: TableCounts;
  rules_seq: number;
}

/** T12 response. */
export interface TableCellListResponse {
  decisions: TableCellDecision[];
  has_more: boolean;
}

/** Error codes in `detail.code` from the /tables API. */
export type TableErrorCode =
  | 'not_table_job'
  | 'not_ready'
  | 'read_only'
  | 'too_many_cell_decisions'
  | 'duplicate_rule'
  | 'not_deletable'
  | 'no_redacted_version'
  | 'rule_failed';

/** Narrow a document's metadata to the table-mode shape, or null. */
export function getTableMode(doc: { metadata?: Record<string, unknown> } | null | undefined): TableModeMetadata | null {
  const value = doc?.metadata?.table_mode;
  if (!value || typeof value !== 'object') return null;
  return value as TableModeMetadata;
}

/**
 * Whether a document opens in the table viewer. The current job's
 * `review_mode` decides; the `table_mode` marker is only the fallback while no
 * job is loaded (a CSV reprocessed in document mode, kill switch off, keeps a
 * stale marker on older data and would 409 in the table view).
 */
export function isTableReview(
  doc: { metadata?: Record<string, unknown> } | null | undefined,
  reviewMode: ReviewMode | null | undefined,
): boolean {
  if (reviewMode) return reviewMode === 'table';
  return getTableMode(doc) !== null;
}
